/**
 * useTagesverlauf Hook
 *
 * Manages state and handlers for the Tagesverlauf component.
 * Extracted to reduce component complexity.
 */

import { useState, useMemo, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useAuth } from "../../../../contexts/AuthContext";
import { useDebug } from "../../../../debug";
import type { AuthContextValue } from "../../shared/types";
import {
  deleteSingleDayAbsenceNote,
  updateAttendanceLogsBatch,
  deleteAttendanceLog,
  syncDailyLogStatus,
  updateTripAttendance,
  cleanupOrphanedNotes,
  determineDailyStatusFromLessons,
  hasAnyAbsentLesson,
  getLatenessDbStatus,
  buildLatenessNotes,
  type UpdateAttendanceLogParams,
} from "../api";
import { attendanceApi } from "../../../attendance/api/attendanceApi";
import type { TimelineLesson } from "../../shared/api/timeline";

interface LatenessInfo {
  minutes: number;
  excused: boolean;
}

interface UseTagesverlaufOptions {
  lessons: TimelineLesson[];
  studentId: string;
  dateIso?: string;
  onUpdate: (lessons: TimelineLesson[]) => void;
  onDeleteNote?: () => void;
}

export function useTagesverlauf(options: UseTagesverlaufOptions) {
  const { lessons, studentId, dateIso, onUpdate, onDeleteNote } = options;
  const { userProfile } = useAuth() as AuthContextValue;
  const { isDebugMode } = useDebug();
  const dispatch = useDispatch();

  // UI State
  const [isOpen, setIsOpen] = useState(false);
  const [changes, setChanges] = useState<Record<string, string>>({});
  const [latenessDetails, setLatenessDetails] = useState<
    Record<string, LatenessInfo>
  >({});
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [openLatenessPopover, setOpenLatenessPopover] = useState<string | null>(
    null,
  );
  const [deletingAttendanceLessonId, setDeletingAttendanceLessonId] = useState<
    string | null
  >(null);
  const [showDeleteAttendanceConfirm, setShowDeleteAttendanceConfirm] =
    useState(false);
  const [lastBulkAction, setLastBulkAction] = useState<{
    status: string;
    timestamp: number;
  } | null>(null);
  // Find the ad-hoc trip lesson's status from the attendance log.
  // The trip lesson has classAbsenceId AND no subject/title (empty string).
  // Regular lessons also have classAbsenceId from the CROSS JOIN, but they have titles.
  const initialTripStatus = useMemo(() => {
    // Trip lesson: has classAbsenceId AND no real subject (title is '—' or empty)
    const tripLesson = lessons.find(
      (l) => l.classAbsenceId && l.status && (l.title === "—" || !l.title),
    );
    return tripLesson?.status || null;
  }, [lessons]);
  const [tripAttendanceStatus, setTripAttendanceStatus] = useState<
    string | null
  >(null);
  // "cleared" = user explicitly deleted → don't fall back to initialTripStatus
  // null = not set → fall back to initialTripStatus (from DB)
  // any value = user selected from dropdown
  const effectiveTripAttendanceStatus =
    tripAttendanceStatus === "cleared"
      ? null
      : tripAttendanceStatus ?? initialTripStatus;
  const [savingTripAttendance, setSavingTripAttendance] = useState(false);

  const hasChanges = Object.keys(changes).length > 0;

  const isSuperAdmin = useMemo(() => {
    const roleName = String(userProfile?.role || "").toLowerCase();
    const subRoles = Array.isArray(userProfile?.sub_roles)
      ? userProfile.sub_roles
          .map((r) => (typeof r === "string" ? r : r?.name))
          .filter(Boolean)
          .map((n) => String(n).toLowerCase())
      : [];
    return roleName === "super admin" || subRoles.includes("super admin");
  }, [userProfile]);

  // Handlers
  const setLessonStatus = useCallback((lessonId: string, status: string) => {
    setChanges((prev) => ({ ...prev, [lessonId]: status }));
    if (status !== "late") {
      setLatenessDetails((prev) => {
        const next = { ...prev };
        delete next[lessonId];
        return next;
      });
    }
  }, []);

  const setLatenessInfo = useCallback(
    (lessonId: string, minutes: number, excused: boolean) => {
      setChanges((prev) => ({ ...prev, [lessonId]: "late" }));
      setLatenessDetails((prev) => ({
        ...prev,
        [lessonId]: { minutes, excused },
      }));
      setOpenLatenessPopover(null);
    },
    [],
  );

  const setAllStatus = useCallback(
    (status: string, options?: { forceOverwrite?: boolean }) => {
      const now = Date.now();
      const forceOverwrite = options?.forceOverwrite === true;
      const isSecondClick =
        lastBulkAction &&
        lastBulkAction.status === status &&
        now - lastBulkAction.timestamp < 3000;

      // Helper: is this lesson on excursion? Skip those for bulk actions.
      const isOnExcursion = (l: TimelineLesson) => {
        if (!l.classAbsenceId) return false;
        if (!l.classAbsenceStartTime || !l.classAbsenceEndTime) return true; // full-day trip
        return (
          (l.start ?? "") < l.classAbsenceEndTime &&
          (l.end ?? "") > l.classAbsenceStartTime
        );
      };

      // Helper: is this lesson still in the future? Bulk actions
      // must not pre-stamp absences on classes that haven't started
      // yet — matches the per-row guard in Tagesverlauf.tsx.
      const lessonDateIso = dateIso ? dateIso.slice(0, 10) : "";
      const isFutureLesson = (l: TimelineLesson) => {
        if (!lessonDateIso || !l.start) return false;
        const ts = new Date(`${lessonDateIso}T${l.start}`).getTime();
        return Number.isFinite(ts) && ts > now;
      };

      // Only apply to non-excursion, non-future lessons
      const eligibleLessons = lessons.filter(
        (l) => l.lesson_id && !isOnExcursion(l) && !isFutureLesson(l),
      );
      const newChanges: Record<string, string> = {};

      if (forceOverwrite || isSecondClick) {
        // Full overwrite — used by the klärung popup's auto-bulk so
        // picking 'Informiert' flips every past lesson (including
        // ones already marked 'Ungeklärt'), and by the standard
        // second-click pattern.
        eligibleLessons.forEach((l) => {
          if (l.lesson_id) newChanges[l.lesson_id] = status;
        });
        setLastBulkAction(null);
      } else {
        // "Empty" must honour already-staged changes, not just the DB
        // status on `lessons`. Otherwise the first click stamps the
        // truly-empty cells but those cells stay "empty" from the
        // filter's POV (we don't mutate `lessons` itself), so every
        // subsequent click outside the 3s second-click window re-fills
        // the same empty cells and never reaches the overwrite branch
        // — leaving any pre-existing Ungeklärt lessons stuck and the
        // parent's mismatch banner stuck along with them.
        const effectiveStatus = (l: TimelineLesson): string | null =>
          l.lesson_id && changes[l.lesson_id]
            ? changes[l.lesson_id]
            : l.status ?? null;
        const emptyLessons = eligibleLessons.filter((l) => {
          const s = effectiveStatus(l);
          return !s || s === "nicht_eingetragen" || s === "";
        });

        if (emptyLessons.length > 0) {
          emptyLessons.forEach((l) => {
            if (l.lesson_id) newChanges[l.lesson_id] = status;
          });
          setLastBulkAction({ status, timestamp: now });
        } else {
          eligibleLessons.forEach((l) => {
            if (l.lesson_id) newChanges[l.lesson_id] = status;
          });
          setLastBulkAction(null);
        }
      }

      setChanges((prev) => ({ ...prev, ...newChanges }));
    },
    [lessons, lastBulkAction, changes, dateIso],
  );

  const saveChanges = useCallback(async () => {
    if (!studentId || !hasChanges) return;
    setSaving(true);
    try {
      const updates: UpdateAttendanceLogParams[] = Object.entries(changes).map(
        ([lessonId, newStatus]) => {
          const lateInfo = latenessDetails[lessonId];
          const notes = lateInfo
            ? buildLatenessNotes(lateInfo.minutes, lateInfo.excused)
            : undefined;
          const dbStatus =
            newStatus === "late" && lateInfo
              ? getLatenessDbStatus(lateInfo.excused)
              : newStatus;

          return {
            studentId,
            lessonId,
            status: dbStatus,
            notes,
            latenessMinutes: lateInfo?.minutes,
          };
        },
      );

      await updateAttendanceLogsBatch(updates);

      if (dateIso) {
        dispatch(
          attendanceApi.util.invalidateTags([
            { type: "AttendanceSummary", id: `${studentId}-${dateIso}` },
            { type: "TimelineLessons", id: `${studentId}-${dateIso}` },
            { type: "Fehlzeiten", id: "LIST" },
          ]),
        );
      }

      const nextLessons = lessons.map((l) => {
        if (l.lesson_id && changes[l.lesson_id]) {
          const lateInfo = latenessDetails[l.lesson_id];
          return {
            ...l,
            status: changes[l.lesson_id],
            notes: lateInfo
              ? buildLatenessNotes(lateInfo.minutes, lateInfo.excused)
              : l.notes,
          };
        }
        return l;
      });

      if (dateIso) {
        const nextDailyStatus = determineDailyStatusFromLessons(nextLessons);
        if (nextDailyStatus) {
          await syncDailyLogStatus({
            studentId,
            date: dateIso,
            presenceStatus: nextDailyStatus,
          });
        }

        if (!hasAnyAbsentLesson(nextLessons)) {
          await cleanupOrphanedNotes(studentId, dateIso);
        }
      }

      onUpdate(nextLessons);
      setChanges({});
      setLatenessDetails({});
    } catch (err) {
      console.error("Error saving:", err);
      alert("Fehler beim Speichern der Anwesenheit");
    } finally {
      setSaving(false);
    }
  }, [
    studentId,
    hasChanges,
    changes,
    latenessDetails,
    lessons,
    dateIso,
    onUpdate,
    dispatch,
  ]);

  const handleDeleteNote = useCallback(async () => {
    if (!dateIso) {
      setDeleteError("Datum fehlt - kann Eintrag nicht löschen");
      return;
    }

    setDeleting(true);
    setDeleteError(null);
    try {
      const result = await deleteSingleDayAbsenceNote(studentId, dateIso);

      if (!result.success) {
        throw new Error(result.error || "Fehler beim Löschen");
      }

      setShowDeleteConfirm(false);
      onDeleteNote?.();
    } catch (err) {
      console.error("Error deleting absence note:", err);
      setDeleteError("Fehler beim Löschen des Eintrags");
    } finally {
      setDeleting(false);
    }
  }, [studentId, dateIso, onDeleteNote]);

  const handleDeleteCancelledLessonAttendance = useCallback(async () => {
    if (!studentId || !deletingAttendanceLessonId) return;

    try {
      const result = await deleteAttendanceLog(
        studentId,
        deletingAttendanceLessonId,
      );
      if (result.success) {
        const updatedLessons = lessons.map((l) =>
          l.lesson_id === deletingAttendanceLessonId
            ? {
                ...l,
                status: null,
                attendanceRecordedByName: null,
                attendanceRecordedAt: null,
              }
            : l,
        );
        onUpdate(updatedLessons);
        dispatch(
          attendanceApi.util.invalidateTags([
            "Timeline",
            "TimelineLessons",
            "DailyLog",
          ]),
        );
      } else {
        alert("Fehler beim Löschen der Anwesenheitsdaten");
      }
    } catch (err) {
      console.error("[Tagesverlauf] Failed to delete attendance:", err);
      alert("Fehler beim Löschen der Anwesenheitsdaten");
    } finally {
      setShowDeleteAttendanceConfirm(false);
      setDeletingAttendanceLessonId(null);
    }
  }, [studentId, deletingAttendanceLessonId, lessons, onUpdate, dispatch]);

  const handleTripAttendanceChange = useCallback(
    async (newStatus: string) => {
      if (!studentId || !dateIso) return;

      const classAbsenceId = lessons[0]?.classAbsenceId || null;

      setSavingTripAttendance(true);
      setTripAttendanceStatus(newStatus);

      try {
        const result = await updateTripAttendance(
          studentId,
          dateIso,
          newStatus,
          classAbsenceId,
        );
        if (result.success) {
          const updatedLessons = lessons.map((l) => ({
            ...l,
            dailyPresenceStatus: newStatus,
            dailyIsTripAttendance: true,
            // Also set status on the trip lesson so trash can shows immediately
            ...(l.title === "—" || !l.title ? { status: newStatus } : {}),
          }));
          onUpdate(updatedLessons);
          dispatch(
            attendanceApi.util.invalidateTags([
              "TimelineLessons",
              "DailyLog",
              "Timeline",
              "Fehlzeiten",
            ]),
          );
        } else {
          setTripAttendanceStatus(null);
          alert("Fehler beim Speichern der Ausflug-Anwesenheit");
        }
      } catch (err) {
        console.error("[Tagesverlauf] Failed to update trip attendance:", err);
        setTripAttendanceStatus(null);
        alert("Fehler beim Speichern der Ausflug-Anwesenheit");
      } finally {
        setSavingTripAttendance(false);
      }
    },
    [studentId, dateIso, lessons, onUpdate, dispatch],
  );

  const handleDeleteExcursionLessonAttendance = useCallback(
    async (lessonId: string) => {
      if (!studentId || !lessonId) return;

      try {
        const result = await deleteAttendanceLog(studentId, lessonId);
        if (result.success) {
          // Check if we're deleting the trip lesson's attendance
          const deletedLesson = lessons.find((l) => l.lesson_id === lessonId);
          if (deletedLesson?.classAbsenceId) {
            setTripAttendanceStatus("cleared");
          }
          const updatedLessons = lessons.map((l) =>
            l.lesson_id === lessonId
              ? {
                  ...l,
                  status: null,
                  dailyPresenceStatus: null,
                  attendanceRecordedByName: null,
                  attendanceRecordedAt: null,
                }
              : l,
          );
          onUpdate(updatedLessons);
          dispatch(
            attendanceApi.util.invalidateTags([
              "TimelineLessons",
              "DailyLog",
              "Timeline",
              "Fehlzeiten",
            ]),
          );
        } else {
          alert("Fehler beim Löschen der Anwesenheitsdaten");
        }
      } catch (err) {
        console.error(
          "[Tagesverlauf] Failed to delete excursion lesson attendance:",
          err,
        );
        alert("Fehler beim Löschen der Anwesenheitsdaten");
      }
    },
    [studentId, lessons, onUpdate, dispatch],
  );

  const getEffectiveStatus = useCallback(
    (lessonId: string | null, original: string | null) =>
      lessonId && changes[lessonId] ? changes[lessonId] : original,
    [changes],
  );

  const isSecondClickReady = useCallback(
    (status: string): boolean => {
      const now = Date.now();
      return !!(
        lastBulkAction &&
        lastBulkAction.status === status &&
        now - lastBulkAction.timestamp < 3000
      );
    },
    [lastBulkAction],
  );

  return {
    // State
    isOpen,
    setIsOpen,
    changes,
    latenessDetails,
    setLatenessDetails,
    saving,
    deleting,
    showDeleteConfirm,
    setShowDeleteConfirm,
    deleteError,
    openLatenessPopover,
    setOpenLatenessPopover,
    deletingAttendanceLessonId,
    setDeletingAttendanceLessonId,
    showDeleteAttendanceConfirm,
    setShowDeleteAttendanceConfirm,
    tripAttendanceStatus: effectiveTripAttendanceStatus,
    savingTripAttendance,
    hasChanges,
    isSuperAdmin,
    isDebugMode,

    // Handlers
    setLessonStatus,
    setLatenessInfo,
    setAllStatus,
    saveChanges,
    handleDeleteNote,
    handleDeleteCancelledLessonAttendance,
    handleTripAttendanceChange,
    handleDeleteExcursionLessonAttendance,
    getEffectiveStatus,
    isSecondClickReady,
  };
}
